import styles from './LabeledInputWithTextBuildingBlock.module.scss';

import { Popover } from '@mui/material';
import { useEventCallback } from '@mui/material/utils';
import React, { Fragment, useRef, useState } from 'react';

import { ReactComponent as TextBuildingBlockIcon } from '@work4all/assets/icons/text-building-block.svg';

import { TextBuildingBlock } from '@work4all/models/lib/Classes/TextBuildingBlock.entity';

import { TextBuildingBlockPicker } from '../../components/entity-picker/TextBuildingBlockPicker';
import { ILabeledInput, LabeledInput } from '../labeled-input/LabeledInput';

interface Props extends Omit<ILabeledInput, 'value' | 'onChange'> {
  value: string;
  onChange(value: string);
  showTextBuildingBlockAction?: boolean;
}

export const LabeledInputWithTextBuildingBlock = ({
  value = '',
  onChange,
  showTextBuildingBlockAction = true,
  ...rest
}: Props) => {
  const iconRef = useRef(null);
  const inputRef = useRef(null);

  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const openPickerHandler = useEventCallback(() => {
    setIsPickerOpen(true);
  });

  const closePickerHandler = useEventCallback(() => {
    setIsPickerOpen(false);
  });

  const onChangeHandler = useEventCallback(
    ({ textBuildingBlock }: { textBuildingBlock: TextBuildingBlock }) => {
      setIsPickerOpen(false);

      if (!textBuildingBlock?.text || !inputRef.current) return;

      const start = inputRef.current.selectionStart;
      const end = inputRef.current.selectionEnd;
      const note =
        value.substring(0, start) +
        textBuildingBlock.text +
        value.substring(end);

      onChange(note);

      const normalizedText = textBuildingBlock.text.replace(/\r\n/g, '\n');
      const cursorPosition = start + normalizedText.length;

      setTimeout(() => {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
      }, 0);
    }
  );

  return (
    <Fragment>
      <Popover
        open={isPickerOpen}
        anchorEl={iconRef.current}
        onClose={closePickerHandler}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <TextBuildingBlockPicker
          onChange={onChangeHandler}
          multiple={false}
          value={null}
        />
      </Popover>

      <LabeledInput
        {...rest}
        ref={inputRef}
        value={value}
        // @ts-expect-error TECH_DEBT
        actions={
          showTextBuildingBlockAction
            ? [
                {
                  icon: (
                    <TextBuildingBlockIcon
                      ref={iconRef}
                      className={styles.icon}
                    />
                  ),
                  onClick: openPickerHandler,
                },
              ]
            : undefined
        }
        classes={{ actions: styles.actions }}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      />
    </Fragment>
  );
};
